// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import {
  AllyConfig,
  CustomWidgetStyles,
  LoanProprosal,
} from './addi-product-widget.d';

import {
  DOWNPAYMENTS_TERM,
  MAX_PREAPPROVAL_AMOUNT,
  QUERY_PARAM_TO_PREAPPROVAL,
} from '../../constants';
import {
  formatCurrency,
  generateRandomID,
  getCookie,
  getDefaultAllyConfig,
  getDefaultLoanProposal,
  getDefaultPagoLoanProposal,
} from '../../utils/utils';
import { getAllyConfigCo } from '../../adapters';
import { DEFAULT_WIDGET_STYLES } from './addi-product-widget.default-styles';
import { getToggle } from '../../feature-toggles/toggle';
import { postHealthCheck } from '../../adapters/channels/health-check';
import { HEALTH_CHECK_TYPE } from '../../constants/health-check';

@Component({
  tag: 'addi-product-widget',
  styleUrl: 'addi-product-widget.scss',
  shadow: true,
})
export class AddiProductWidget {
  @Prop() price: number;
  @Prop() allySlug: string;
  @Prop() customWidgetStyles: string;
  @Prop() country: string;

  @State() loanProposal: LoanProprosal;
  @State() allyConfig: AllyConfig;
  @State() totalInstallment: string;

  @State() preRender = false;
  @State() openModal = false;
  @State() isValidToDisplay = true;
  @State() healthCheckIsEnable = false;
  @State() preApprovalCurrentVersion = 'v1';

  @Element() el: HTMLElement;

  @Watch('price')
  async onPriceChange() {
    await this.onInit();
  }

  widgetStyles: CustomWidgetStyles;
  widgetId: string;
  redirectToPreapproval: boolean;
  showPreapproval = false;
  finalRequestedAmount: number;
  modal;

  getAllyConfig: (
    requestedAmount: number,
    allySlug?: string
  ) => Promise<AllyConfig | undefined>;

  async componentWillLoad() {
    if (this.country === 'co') {
      this.getAllyConfig = getAllyConfigCo;

      await this.onInit();
      const toggles = await getToggle(this.country);
      if (this.allyConfig?.allySlug) {
        this.healthCheckIsEnable = toggles.healthcheckEnable({
          ally_slug: this.allyConfig.allySlug,
        });
        this.preApprovalCurrentVersion = await toggles.preApprovalVersion({
          ally_slug: this.allyConfig.allySlug,
        });
        this.healthCheckValidation(HEALTH_CHECK_TYPE.widget);
      }
    }
  }

  async componentDidLoad() {
    this.setModalReference();
    this.appendModalToPageBody();
  }

  async componentDidUpdate() {
    this.setModalReference();
  }

  setModalReference(): void {
    if (!this.modal) {
      this.modal = this.el.shadowRoot.querySelector(
        `#addi-product-modal_${this.widgetId}`
      );
    }
  }

  appendModalToPageBody(): void {
    if (this.modal) {
      const pageBody = document.querySelector('body');
      pageBody.append(this.modal);
    }
  }

  healthCheckValidation(type: HEALTH_CHECK_TYPE): void {
    if (!this.allySlug) {
      return;
    }

    const statusValidated = getCookie('statusValidated');
    if (this.healthCheckIsEnable && this.allySlug && !statusValidated) {
      postHealthCheck(this.allySlug, type);
      const expires = new Date(new Date().getTime() + 3600000);
      document.cookie = `statusValidated=true; expires=${expires.toUTCString()}`;
    }
  }

  handleOpenModalClick(event: UIEvent) {
    event.preventDefault();

    this.modal.open();
  }

  setInitialConfig(): void {
    this.preRender = true;

    this.widgetId = generateRandomID();

    const customWidgetStyles = this.customWidgetStyles
      ? JSON.parse(this.customWidgetStyles)
      : {};

    this.widgetStyles = {
      widget: {
        ...DEFAULT_WIDGET_STYLES.widget,
        ...(customWidgetStyles?.widget || {}),
      },
      modal: {
        ...DEFAULT_WIDGET_STYLES.modal,
        ...(customWidgetStyles?.modal || {}),
      },
    };
  }

  async getData(): Promise<void> {
    await this.setAllyConfig();
    if (
      this.price >= this.allyConfig.minAmount &&
      this.price <= this.allyConfig.maxAmount
    ) {
      if (!this.isValidToDisplay) this.isValidToDisplay = true;
      await this.getProposalByTemplate();

      const queryParams = new URLSearchParams(window.location.search);
      this.redirectToPreapproval = queryParams.has(QUERY_PARAM_TO_PREAPPROVAL);
    } else {
      console.warn('[addi-widget] Price is out of bound');
      this.isValidToDisplay = false;
    }
  }

  async setAllyConfig() {
    const allyConfig =
      (await this.getAllyConfig(this.price, this.allySlug)) ||
      getDefaultAllyConfig(this.country);
    this.allyConfig = {
      allySlug: this.allySlug,
      ...allyConfig,
    };
  }

  /**
   * This is a temporary flow redirection
   * to avoid calling so many times loanSimualted endpoint
   * during DSI
   * TODO: Refactor this.
   */
  async getProposalByTemplate(): Promise<void> {
    if (this.country === 'br') {
      this.getLoanProposalBr();
      return;
    }

    return this.getLoanProposal();
  }

  buildLoanProposalLocally(): void {
    const requestedAmount = this.price;
    const discount = this.allyConfig?.policy?.discount;

    this.loanProposal = getDefaultPagoLoanProposal(requestedAmount, discount);
    this.totalInstallment = formatCurrency(this.loanProposal.totalInstallment);
  }

  getLoanProposal(): void {
    // const allySlug = this.allySlug?.toLowerCase();
    const requestedAmount = this.price;

    // const proposal = await getLoanProposal(requestedAmount, allySlug);

    // if (proposal) {
    //   this.loanProposal = {
    //     requestedAmount,
    //     ...proposal,
    //   };
    // } else {
    this.loanProposal = getDefaultLoanProposal(requestedAmount);
    // }

    const { clientLoanSimulated, prospectLoanSimulated } = this.loanProposal;
    const hasDiscount = !!clientLoanSimulated || !!prospectLoanSimulated;

    if (hasDiscount) {
      const totalInstallment = clientLoanSimulated
        ? clientLoanSimulated?.totalInstallment
        : prospectLoanSimulated?.totalInstallment;
      this.totalInstallment = formatCurrency(totalInstallment);
    } else {
      this.totalInstallment = formatCurrency(
        this.loanProposal.totalInstallment
      );
    }
  }

  async getLoanProposalBr() {
    const requestedAmount = this.price;
    const term = DOWNPAYMENTS_TERM;

    const loanDiscount = this.allyConfig?.policy?.discount || 0;
    const requestedAmountRecalculated =
      requestedAmount - loanDiscount * requestedAmount;

    const totalInstallment = requestedAmountRecalculated / term;
    this.totalInstallment = formatCurrency(totalInstallment, 'pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    this.loanProposal = {
      requestedAmount: requestedAmount,
      totalInstallment: totalInstallment,
      term: term,
      effectiveAnnualRate: 0,
      totalGuaranteeRate: 0,
    };

    if (loanDiscount) {
      this.loanProposal.prospectLoanSimulated = {
        totalInstallment: totalInstallment,
        requestedAmountWithDiscount: requestedAmountRecalculated,
        discount: loanDiscount,
        minTerm: 3,
        maxTerm: 3,
      };
    }
  }

  setPreapprovalConfig(): boolean {
    const { widgetConfig, policy } = this.allyConfig;
    const isPreapprovalActive = widgetConfig?.widgetShowPreapproval;

    if (policy) {
      const validRequestedAmount =
        this.loanProposal?.requestedAmount <= policy.policyMaxAmount;
      const isAddiPago = policy.productType === 'ADDI_PAGO';

      return validRequestedAmount && isAddiPago && isPreapprovalActive;
    } else {
      const validRequestedAmount =
        this.loanProposal?.requestedAmount <= MAX_PREAPPROVAL_AMOUNT;

      return validRequestedAmount && isPreapprovalActive;
    }
  }

  getLoanProposalAmount(): number {
    const { clientLoanSimulated, prospectLoanSimulated } =
      this.loanProposal || {};
    const hasDiscount = !!clientLoanSimulated || !!prospectLoanSimulated;

    if (hasDiscount) {
      return clientLoanSimulated
        ? clientLoanSimulated.requestedAmountWithDiscount
        : prospectLoanSimulated.requestedAmountWithDiscount;
    } else {
      return this.loanProposal?.requestedAmount;
    }
  }

  async onInit(): Promise<void> {
    this.setInitialConfig();
    await this.getData();

    this.showPreapproval = this.setPreapprovalConfig();
    this.finalRequestedAmount = this.getLoanProposalAmount();

    if (!isNaN(this.price)) {
      this.preRender = false;
    }
  }

  render() {
    return this.country === 'co' ? (
      <Host id={`addi-product-widget_${this.widgetId}`}>
        {this.isValidToDisplay &&
          this.allyConfig.isActiveAlly && [
            <addi-product-banner
              preRender={this.preRender}
              widgetId={`addi-banner_${this.widgetId}`}
              customBannerStyle={this.widgetStyles.widget}
              version={this.allyConfig?.widgetConfig?.widgetVersion}
              preapproval={this.showPreapproval}
              proposal={{
                interests: !!this.loanProposal?.effectiveAnnualRate,
                discount: this.allyConfig?.policy?.discount,
                amount: this.finalRequestedAmount,
                term: this.loanProposal?.term,
                totalInstallment: this.totalInstallment,
              }}
              allySlug={this.allyConfig.allySlug}
              country={this.country}
              onClick={(e) => this.handleOpenModalClick(e)}
              preApprovalVersion={this.preApprovalCurrentVersion}
            />,
            <addi-product-modal
              id={`addi-product-modal_${this.widgetId}`}
              customModalStyle={this.widgetStyles.modal}
              onHealthCheckValidate={(e) =>
                this.healthCheckValidation(e.detail)
              }
              showPreapproval={this.showPreapproval}
              proposal={this.loanProposal}
              allyConfig={this.allyConfig}
              redirectPreapproval={this.redirectToPreapproval}
              country={this.country}
              preapprovalVersion={this.preApprovalCurrentVersion}
            />,
          ]}
      </Host>
    ) : (
      <div></div>
    );
  }
}
